import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useMoveInboxEntryToTaskSubgroup } from './useMoveInboxEntryToTaskSubgroup';
import { useDoBoxMultiSelectStore, useInboxEntriesStore } from '../../store';
import { DOBOX_BULK_I18N_KEYS } from '../doBoxMultiSelectActions.constants';
import { useCustomToast } from '../useCustomToast';

function resolveSubgroupTitle(
  taskSubgroupId: string | null,
  taskSubgroups: { id: string; title: string }[],
): string {
  if (taskSubgroupId === null) {
    return '';
  }
  return taskSubgroups.find((sg) => sg.id === taskSubgroupId)?.title ?? '';
}

function resolveIdsRequiringMove(
  ids: string[],
  targetSubgroupId: string | null,
  inboxEntries: InboxEntry[],
): string[] {
  return ids.filter((id) => {
    const entry = inboxEntries.find((e) => e.id === id);
    return (entry?.taskSubgroupId ?? null) !== targetSubgroupId;
  });
}

export function useMoveInboxEntriesToTaskSubgroup() {
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const { moveToTaskSubgroup } = useMoveInboxEntryToTaskSubgroup();
  const clear = useDoBoxMultiSelectStore((s) => s.clear);

  const moveEntriesToTaskSubgroup = useCallback(
    (ids: string[], targetSubgroupId: string | null) => {
      if (ids.length === 0) {
        return;
      }

      const inboxEntries =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup
          .inboxEntries;
      const idsToMove = resolveIdsRequiringMove(
        ids,
        targetSubgroupId,
        inboxEntries,
      );
      if (idsToMove.length === 0) {
        return;
      }

      const taskSubgroups =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup
          .taskSubgroups;
      const subgroupTitle = resolveSubgroupTitle(
        targetSubgroupId,
        taskSubgroups,
      );

      clear();

      const movePromise = Promise.all(
        idsToMove.map(async (id) => {
          const success = await moveToTaskSubgroup(id, targetSubgroupId);
          if (!success) {
            throw new Error(`Failed to move entry ${id}`);
          }
        }),
      );

      movePromise.catch((error) => {
        log.error('Failed to move entries to subgroup:', error);
      });

      const successMessage =
        targetSubgroupId === null
          ? t(DOBOX_BULK_I18N_KEYS.MOVED_OUT_OF_SUBGROUP, {
              count: idsToMove.length,
            })
          : t(DOBOX_BULK_I18N_KEYS.MOVED_TO_SUBGROUP, {
              count: idsToMove.length,
              subgroup: subgroupTitle,
            });

      promiseToast(
        movePromise,
        t(DOBOX_BULK_I18N_KEYS.MOVE_LOADING),
        successMessage,
        t(DOBOX_BULK_I18N_KEYS.MOVE_ERROR),
      );
    },
    [moveToTaskSubgroup, clear, promiseToast, t],
  );

  return { moveEntriesToTaskSubgroup };
}
